import CAutocomplete from '../../../components/CAutocomplete';
import { type EOptionsCalculatorParameters } from '../../../enums/EOptionsCalculatorParameters';
import CStatefulContainer from '../../../components/CStatefulContainer';
import CTab from '../../../components/CTab';
import CTabItem from '../../../components/CTabItem';
import React from 'react';
import type { IResponseTableOrPlot } from '../../../models/IResponseTableOrPlot';
import type { PlotlyResponse } from '../../../models/PlotlyModel';
import type { IResponse } from '../../../models/IResponse';
import { type IAutocompleteOption } from '../../../models/IAutocompleteOption';
import CPageModulePlot from '../../../components/CPageModulePlot';
import CPageModule from '../../../components/CPageModule';
import CButton from '../../../components/CButton';
import { type I3DPlotConfig } from '../../../models/calculator/I3DPlotConfigModel';
import CLocalizedTextString from '../../../components/CLocalizedTextString';
import { Close } from '@mui/icons-material';
import type { IOptionsCalculatorConfig } from '../../../models/IOptionsCalculatorConfig';
import { BreakpointsInPx } from '../../../enums/BreakpointsInPx';
import { useAppSelector } from '../../../hooks/hooks';

interface Props {
  optionsTableDerivatives: IResponse<{ options: Array<{ label: string, value: string }> }>;
  plot3d: IResponseTableOrPlot<{ call: PlotlyResponse, put: PlotlyResponse }>;
  parameterOptions: IAutocompleteOption[];
  plotConfig: I3DPlotConfig;
  showDeleteButton?: boolean;
  onUpdatePlotConfig: (configUpdate: Partial<I3DPlotConfig>) => void;
  onDeletePlot: () => void;
  textPrefix: string;
  plotIndex: number;
  textSuffix: string;
  optionsConfigLoaded: IOptionsCalculatorConfig;
  optionsConfigInput: IOptionsCalculatorConfig;
  optionsConfigDefault: IOptionsCalculatorConfig;
}

export default function CCalculator3dPlot(props: Props) {
  const { plotConfig, onUpdatePlotConfig, onDeletePlot } = props;
  const { dashboardOutletDimensions } = useAppSelector((state) => state.dashboard);

  const cLocalizedTextStringParameters = CLocalizedTextString('topics.calculator.page.parameters');
  const cLocalizedTextStringPut = CLocalizedTextString('topics.calculator.page.put');
  const cLocalizedTextStringCall = CLocalizedTextString('topics.calculator.page.call');

  const handlePlotXChange = (options: IAutocompleteOption[]) => {
    const newPlot3dX = options[0]?.key as EOptionsCalculatorParameters;
    if (newPlot3dX) {
      onUpdatePlotConfig({ plot3dX: newPlot3dX });
    }
  };

  const handlePlotYChange = (options: IAutocompleteOption[]) => {
    const newPlot3dY = options[0]?.key as EOptionsCalculatorParameters;
    if (newPlot3dY) {
      onUpdatePlotConfig({ plot3dY: newPlot3dY });
    }
  };

  const handlePlotZChange = (options: IAutocompleteOption[]) => {
    const newPlot3dZ = options[0]?.key as EOptionsCalculatorParameters;
    if (newPlot3dZ) {
      onUpdatePlotConfig({ plot3dZ: newPlot3dZ });
    }
  };

  const deleteButton = (
    <CButton
      size={'textfield'}
      icons={{ start: <Close /> }}
      onClick={onDeletePlot}
      isForm={true}
      style={{borderWidth: 1}}/>
  );

  return (<>
    <CPageModule submodule>
      <p>
        {props.textPrefix}{props.textSuffix && props.plotIndex + 1}{props.textSuffix}
      </p>
      <div className={'flex gap-2'}>
        <div className={'grid gap-2 min-w-0 w-full'}
                 style={{ gridTemplateColumns: `repeat(${dashboardOutletDimensions.w <= BreakpointsInPx.BREAKPOINT_1 ? 1 : (dashboardOutletDimensions.w <= BreakpointsInPx.BREAKPOINT_2 ? 2 : 3)}, minmax(0, 1fr))` }}>
          <div style={{gridColumn: dashboardOutletDimensions.w <= BreakpointsInPx.BREAKPOINT_1 ? 'span 2 / span 2' : 'span 1 / span 1'}}>
            <CAutocomplete
              options={props.parameterOptions.filter((e) => e.key !== plotConfig.plot3dY)}
              value={[{
                label: plotConfig.plot3dX,
                key: plotConfig.plot3dX,
              }]}
              textFieldLabel={cLocalizedTextStringParameters}
              onChange={handlePlotXChange}
            />
          </div>
          <div style={{gridColumn: dashboardOutletDimensions.w <= BreakpointsInPx.BREAKPOINT_1 ? 'span 2 / span 2' : 'span 1 / span 1'}}>
          <CAutocomplete
              options={props.parameterOptions.filter((e) => e.key !== plotConfig.plot3dX)}
              value={[{
                label: plotConfig.plot3dY,
                key: plotConfig.plot3dY,
              }]}
              textFieldLabel={cLocalizedTextStringParameters}
              onChange={handlePlotYChange}
            />
          </div>
          <div className={'flex gap-2'}
               style={{gridColumn: dashboardOutletDimensions.w <= BreakpointsInPx.BREAKPOINT_2 ? 'span 2 / span 2' : 'span 1 / span 1'}}>
            <div className={'flex-grow'}>
              <CAutocomplete
                options={
                  props.optionsTableDerivatives.data?.options.map((e) => {
                    return { label: e.label, key: e.value };
                  }) ?? []
                }
                value={[{
                  label: plotConfig.plot3dZ,
                  key: plotConfig.plot3dZ,
                }]}
                textFieldLabel={cLocalizedTextStringParameters}
                onChange={handlePlotZChange}
              />
            </div>
            {
              props.showDeleteButton && dashboardOutletDimensions.w <= BreakpointsInPx.BREAKPOINT_2 && deleteButton
            }
          </div>
        </div>
        {props.showDeleteButton && dashboardOutletDimensions.w > BreakpointsInPx.BREAKPOINT_2 && deleteButton}
      </div>
    </CPageModule>
    <CStatefulContainer submodule apiRequestState={props.plot3d.state}>
      <CTab submodule>
        <CTabItem label="Call" text={cLocalizedTextStringCall}>
          <CPageModulePlot
            showWhen={true}
            clickToInteract
            aspectRatio={window.innerWidth <= BreakpointsInPx.BREAKPOINT_2 ? 2 : 1.5}
            data={props.plot3d.data?.call?.data}
            layout={props.plot3d.data?.call?.layout} />
        </CTabItem>
        <CTabItem label="Put" text={cLocalizedTextStringPut}>
          <CPageModulePlot
            showWhen={true}
            aspectRatio={window.innerWidth <= BreakpointsInPx.BREAKPOINT_2 ? 2 : 1.5}
            clickToInteract
            data={props.plot3d.data?.put?.data}
            layout={props.plot3d.data?.put?.layout} />
        </CTabItem>
      </CTab>
    </CStatefulContainer>
  </>);
}
